import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { LessonCarouselService } from '../../home/lesson-carousel/lesson-carousel.service';
import { ReferralPageBannerService } from '../../home/referral-page-banner/referral-page-banner.service';
import { ActivityPlayerEvent } from '../../shared/activity-player/activity-player-event.enum';
import { ImmersionCourseService } from '../../shared/immersion-course.service';
import { LessonPlayerService } from '../../shared/lesson-player.service';
import { LevelProgressService } from '../../shared/level-progress/level-progress.service';
import { OeAppPersonService } from '../../shared/oe-app-person.service';
import { OeAppService } from '../../shared/oe-app.service';
import { LevelProgressDto } from '../../shared/types/LevelProgressDto';
import { NextLessonButtonType } from '../../shared/types/NextButtonActionType';
import { StudyBundleCourseDto } from '../../shared/types/StudyBundleCourseDto';
import { LessonType } from '../../shared/types/lesson-type';
import { WindowService } from '../../shared/window.service';
import { MidLevelExamModalComponent } from '../mid-level-exam-modal/mid-level-exam-modal.component';
import { BreadcrumbTitle, getBreadcrumbTitle } from './lesson-breadcrumb-utils';
import { LessonPageParams } from './lesson-page-params';
import { PlayerViewerType } from '../../shared/types/player-viewer-type.enum';
import { ChatBotStateService } from '../../shared/chat-bot-state.service';
import { ChatBotTypes } from '../../shared/types/chat-bot-types.enum';

@Component({
  selector: 'lp2-lesson-page',
  templateUrl: './lesson-page.component.html',
  styleUrls: ['./lesson-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LessonPageComponent implements OnInit, OnDestroy {
  activityUrl$ = new BehaviorSubject<SafeUrl>(null);
  course: StudyBundleCourseDto;

  private userProgress: LevelProgressDto;
  private subscriptions = new Subscription();
  private params: LessonPageParams;
  private lessonNumber: number;
  private lessons: Array<StudyBundleCourseDto>;
  public copilotEnabled: boolean;
  public chatBotState$ = this.chatBotStateService.state$;
  public chatBotTypes = ChatBotTypes

  @ViewChild('iframe') iframe: ElementRef;

  constructor(
    private lessonPlayerService: LessonPlayerService,
    private lessonCarouselService: LessonCarouselService,
    private immersionCourseService: ImmersionCourseService,
    private oeApp: OeAppService,
    private oeAppPersonService: OeAppPersonService,
    private windowService: WindowService,
    private levelProgressService: LevelProgressService,
    private modalService: NgbModal,
    private referralBannerService: ReferralPageBannerService,
    private readonly chatBotStateService: ChatBotStateService
  ) {}

  ngOnInit() {
    this.copilotEnabled = this.oeAppPersonService.copilotEnabled;
    this.params = new LessonPageParams(this.windowService.getSearchParams().get('q'));

    this.getLessonDataWithBookmark(
      this.params.courseId,
      this.params.unitId,
      this.oeAppPersonService.lessonType
    ).subscribe((course) => {
      if (!course || !course.mediaData) {
        return this.windowService.redirectToUrl('/');
      }

      const { mediaData: lessonId, bookmark, id: courseId } = course;
      this.setActivityUrl(lessonId, bookmark, courseId);
      this.course = course;

      this.lessonCarouselService.getLessons(this.params.unitId).subscribe((data) => {
        this.lessonNumber = data.findIndex((lesson) => lesson.id === course.id) + 1;
        this.lessons = data;
      });
    });

    this.subscriptions.add(
      this.levelProgressService.levelProgress$.subscribe(
        (userProgress: LevelProgressDto) => (this.userProgress = userProgress)
      )
    );
    this.levelProgressService.updateLevelProgress();

    this.windowService.nativeWindow().addEventListener('message', this.handlePlayerEvents.bind(this));

    // Uncomment to complete the lesson and go back to home automatically
    // setTimeout(() => {
    //   console.log('this.course', this.course)
    //   window.parent.postMessage({type: 'lessonComplete'}, '*');
    //   setTimeout(()=> window.location.href = 'http://localhost:8082', 500);
    // }, 1000);
  }

  ngOnDestroy() {
    this.windowService.nativeWindow().removeEventListener('message', this.handlePlayerEvents);
    this.subscriptions.unsubscribe();
  }

  private getLessonData(courseId?: number, unitId?: number, lessonType?: StudyBundleCourseDto['lessonType']) {
    if (courseId) {
      return this.lessonCarouselService.getMiniLesson({ courseId, unitId });
    }

    return this.lessonCarouselService.getCurrentMiniLesson(lessonType);
  }

  closeChatBotModal() {
    this.chatBotStateService.closeChatBotMiniLessonModal();
  }

  private getLessonDataWithBookmark(
    courseId?: number,
    unitId?: number,
    lessonType?: StudyBundleCourseDto['lessonType']
  ): Observable<(StudyBundleCourseDto & { bookmark: number }) | null> {
    return this.getLessonData(courseId, unitId, lessonType).pipe(
      mergeMap((course) => {
        if (!course) {
          return of(null);
        }

        return this.immersionCourseService
          .retrieveBookmark(course.id)
          .pipe(map((bookmark) => Object.assign({}, course, { bookmark })));
      })
    );
  }

  private setActivityUrl(lessonId: string, bookmark: number, courseId: number) {
    this.referralBannerService.isElligible$.subscribe((isElligible) => {
      if (isElligible !== undefined) {
        this.lessonPlayerService
          .getActivityUrl$(
            lessonId,
            PlayerViewerType.LESSON,
            isElligible,
            bookmark,
            this.oeAppPersonService.lessonType,
            courseId
          )
          .subscribe((activityUrl) => {
            this.activityUrl$.next(activityUrl);
          });
      }
    });
  }

  updateBookmark(contentId: number, slideIndex: number) {
    this.immersionCourseService.saveBookmark(contentId, slideIndex).subscribe();
  }

  postMessage(payload) {
    this.iframe.nativeElement.contentWindow.postMessage(payload, '*');
  }

  private getUnitName(userProgress: LevelProgressDto): string {
    return (
      (this.params.unitName && this.params.unitName.toUpperCase()) ||
      `UNIT ${
        userProgress && userProgress.unitTaken ? Math.min(userProgress.unitRequired, userProgress.unitTaken + 1) : 1
      }`
    );
  }

  private getLevelName(userProgress: LevelProgressDto): string {
    return (
      (this.params.levelName && this.params.levelName.toUpperCase()) ||
      `LEVEL ${(userProgress && userProgress.levelNumber) || 0}`
    );
  }

  sendLessonInfo() {
    const levelName = this.getLevelName(this.userProgress);
    const unitName = this.getUnitName(this.userProgress);
    const breadcrumb: BreadcrumbTitle = getBreadcrumbTitle(this.course.title, this.lessonNumber, levelName, unitName);
    this.postMessage({
      type: ActivityPlayerEvent.SEND_LESSON_INFO,
      value: {
        id: this.course.id,
        description: this.course.description,
        ...breadcrumb,
        nextLessonButtonType: this.defineNextLessonButtonType(),
      },
    });
  }

  defineNextLessonButtonType() {
    const lessonIndex = this.lessons.findIndex((l) => l.id === this.course.id);
    if (this.params.isFromUnitIndex) {
      return NextLessonButtonType.UNIT_INDEX;
    }
    if (lessonIndex < this.lessons.length - 1) {
      return NextLessonButtonType.NEXT_LESSON;
    }
    if (this.userProgress.unitTaken + 1 >= this.userProgress.unitRequired) {
      return null;
    }
    return NextLessonButtonType.NEXT_UNIT;
  }

  sendStudentInfo() {
    const person = this.oeAppPersonService.getPersonData();
    this.postMessage({
      type: ActivityPlayerEvent.SEND_STUDENT_INFO,
      value: {
        id: person.id,
        name: `${person.firstName} ${person.lastName}`,
        image: `${this.windowService.getDomain()}/profile/image/123/123`,
        contactId: person.contactId,
      },
    });
  }

  handlePlayerEvents({ data, origin }) {
    if (
      origin !== this.oeApp.activityPlayerHost() &&
      origin !== 'http://localhost:8081' &&
      !this.oeApp.webLessonPlayerPageHost.includes(origin)
    ) {
      return;
    }

    if (!data || !data.type) {
      return;
    }

    let eventData = data;
    if (this.oeApp.webLessonPlayerPageHost.includes(origin)) {
      eventData = data.data || {};
    }

    this.handleEvent(data.type, eventData.index, eventData.action);
  }

  async handleEvent(type: ActivityPlayerEvent, index: number, action: string) {
    switch (type) {
      case ActivityPlayerEvent.ACTIVITY_COMPLETE:
        this.updateBookmark(this.course.id, index + 1);
        if (index === 0) {
          this.lessonCarouselService.initCourse(this.course.id);
        }
        break;
      case ActivityPlayerEvent.REQUEST_LESSON_INFO:
        this.sendLessonInfo();
        break;
      case ActivityPlayerEvent.REQUEST_STUDENT_INFO:
        this.sendStudentInfo();
        break;
      case ActivityPlayerEvent.REFERRAL_BUTTON_CLICKED:
        this.referralBannerService.redirectToReferral();
        break;
      case ActivityPlayerEvent.LESSON_COMPLETE:
        this.lessonCarouselService.completeLesson(this.course.id).subscribe(() => {
          if (this.course.type === LessonType.UNIT_ASSESSMENT) {
            this.lessonCarouselService.finishAssessment(this.course.id).subscribe();
          }
        });
        break;
      case ActivityPlayerEvent.FINISH_LESSON:
        let defaultUrl: string;

        switch (action) {
          case NextLessonButtonType.UNIT_INDEX:
            defaultUrl = '/learning/unit-index.html';
            break;
          default:
            defaultUrl = action === 'next_lesson' ? this.windowService.getLocationHrefWithoutQueries() : '/';
            break;
        }

        if (this.course.type === LessonType.UNIT_ASSESSMENT) {
          const nextUrl = (await this.shouldGoToMidLevelTest())
            ? `/mid-level-exam.html?levelId=${this.oeAppPersonService.getPersonData().levelId}`
            : defaultUrl;
          this.windowService.redirectToUrl(nextUrl);
        } else {
          this.windowService.redirectToUrl(defaultUrl);
        }
        break;
      case ActivityPlayerEvent.NAVIGATE_HOME:
        this.windowService.redirectToUrl('/');
    }
  }

  private shouldGoToMidLevelTest(): Promise<boolean> {
    return new Promise((resolve) => {
      this.levelProgressService.getShowMidLevelTest().subscribe(async (showMidLevelTest) => {
        if (showMidLevelTest) {
          const goToMidLevelTest = await this.askIfWantToTakeMidLevelTest();
          resolve(goToMidLevelTest);
        }
        resolve(false);
      });
    });
  }

  private async askIfWantToTakeMidLevelTest(): Promise<any> {
    const modalRef = this.modalService.open(MidLevelExamModalComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });

    return modalRef.result;
  }
}



// WEBPACK FOOTER //
// ./ts/app/learning/lesson-page/lesson-page.component.ts